import type { AxiosError } from 'axios';
import axios from 'axios';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { HIGH_RISK_COLOR, LOW_RISK_COLOR, MEDIUM_RISK_COLOR } from '../../../../../constants';
import type { ErrorMessage } from '../../../../../models/error';
import type { AsyncThunkConfig } from '../../../../../models/slice';
import { RaygunErrorHandlerService } from '../../../../../service/raygun.service';
import type { TrendType } from './trends';

const { logError } = RaygunErrorHandlerService();

export type Trend = {
  field_id?: string;
  measurement_label?: string;
  entries: { time: string; value: number }[];
};

// TODO: filter critical risk to orgs that use it
export type Status = 'critical_risk' | 'high_risk' | 'medium_risk' | 'low_risk' | 'inactive' | 'incomplete' | 'archive';

export type StatusTime = {
  name: Status;
  value: number;
};

export type DonutData = (
  | {
      status: 'critical_risk';
      name: 'Critical risk' | 'Critical';
      itemStyle: {
        color: typeof HIGH_RISK_COLOR;
      };
    }
  | {
      status: 'high_risk';
      name: 'High risk' | 'High';
      itemStyle: {
        color: typeof HIGH_RISK_COLOR;
      };
    }
  | {
      status: 'medium_risk';
      name: 'Medium risk' | 'Medium';
      itemStyle: {
        color: typeof MEDIUM_RISK_COLOR;
      };
    }
  | {
      status: 'low_risk';
      name: 'low_risk' | 'Low risk' | 'Low';
      itemStyle: {
        color: typeof LOW_RISK_COLOR;
      };
    }
  | {
      status: 'incomplete';
      name: 'Incomplete';
      itemStyle: {
        color: '#4b4b4b';
      };
    }
  | {
      status: 'inactive';
      name: 'Inactive';
      itemStyle: {
        color: '#4b4b4b';
      };
    }
  | {
      status: 'archive';
      name: 'Archived';
      itemStyle: {
        color: 'black';
      };
    }
) & {
  value: number;
};

type CalendarData = {
  score: number;
  status: Status;
  timestamp: string;
};

export type ChartType = 'calendar' | 'indicators' | 'bam17' | 'donut' | 'pqh9' | 'pharmasmart';

type GraphSliceType = {
  chart: ChartType | undefined;
  trends: Trend[];
  loadingTrends: boolean;
  stateFrequencyData: StatusTime[];
  loadingStateFrequency: boolean;
  calendar: CalendarData[];
  loadingCalendar: boolean;
  donutStartDate: Dayjs;
  donutEndDate: Dayjs;
  calendarStartDate: Dayjs;
  calendarEndDate: Dayjs;
};

const initialState: GraphSliceType = {
  chart: undefined,
  trends: [],
  loadingTrends: false,
  stateFrequencyData: [],
  loadingStateFrequency: false,
  calendar: [],
  loadingCalendar: false,
  donutStartDate: dayjs().subtract(1, 'months'),
  donutEndDate: dayjs(),
  calendarStartDate: dayjs('2019-01'),
  calendarEndDate: dayjs(),
};

export const fetchTrends = createAsyncThunk<Trend[], { client_id: string; type: TrendType }, AsyncThunkConfig>(
  'graph/fetchTrends',
  async ({ client_id, type }, thunkAPI) => {
    try {
      const url =
        type === 'bam17'
          ? 'v2_bam_17_use_field_entries'
          : type === 'phq9'
            ? 'v3_phq_9_use_field_entries'
            : 'v0_pharmasmart_measurement_fields';
      const response = (await axios.post(url, { client_id }, { signal: thunkAPI.signal })) as Trend[];
      return response ?? [];
    } catch (e) {
      logError(e, ['graphSlice', 'fetchTrends']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchStateFrequency = createAsyncThunk<StatusTime[], string, AsyncThunkConfig>(
  'graph/fetchStateFrequency',
  async (client_id, thunkAPI) => {
    try {
      const { donutStartDate, donutEndDate } = thunkAPI.getState().graphSlice;
      const response = (await axios.post(
        'v2_client_state_frequency',
        {
          client_id,
          start_date: donutStartDate,
          end_date: donutEndDate,
        },
        { signal: thunkAPI.signal },
      )) as StatusTime[];
      return response ?? [];
    } catch (e) {
      logError(e, ['graphSlice', 'fetchStateFrequency']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchCalendarHistory = createAsyncThunk<CalendarData[], string, AsyncThunkConfig>(
  'graph/fetchCalendarHistory',
  async (client_id, thunkAPI) => {
    try {
      const { calendarStartDate, calendarEndDate } = thunkAPI.getState().graphSlice;
      const response = (await axios.post(
        'v2_client_risk_history',
        {
          client_id,
          start_date: calendarStartDate,
          end_date: calendarEndDate,
        },
        { signal: thunkAPI.signal },
      )) as CalendarData[];
      return response ?? [];
    } catch (e) {
      logError(e, ['graphSlice', 'fetchCalendarHistory']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const graphSlice = createSlice({
  name: 'graph',
  initialState,
  reducers: {
    selectDates: (state, action: PayloadAction<{ startDate: Dayjs; endDate: Dayjs }>) => {
      const { startDate, endDate } = action.payload;
      state.donutStartDate = startDate;
      state.donutEndDate = endDate;
    },
    selectChart: (state, action: PayloadAction<ChartType | undefined>) => {
      state.chart = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrends.pending, (state, _action) => {
        state.loadingTrends = true;
      })
      .addCase(fetchTrends.fulfilled, (state, action) => {
        state.trends = action.payload;
        state.loadingTrends = false;
      })
      .addCase(fetchTrends.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingTrends = false;
        }
      })
      .addCase(fetchStateFrequency.pending, (state, _action) => {
        state.loadingStateFrequency = true;
      })
      .addCase(fetchStateFrequency.fulfilled, (state, action) => {
        state.stateFrequencyData = action.payload;
        state.loadingStateFrequency = false;
      })
      .addCase(fetchStateFrequency.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingStateFrequency = false;
        }
      })
      .addCase(fetchCalendarHistory.pending, (state, _action) => {
        state.loadingCalendar = true;
      })
      .addCase(fetchCalendarHistory.fulfilled, (state, action) => {
        state.calendar = action.payload;
        state.loadingCalendar = false;
      })
      .addCase(fetchCalendarHistory.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingCalendar = false;
        }
      });
  },
});

export const { selectDates, selectChart } = graphSlice.actions;
